import React, { useEffect, useState } from 'react';
import { formatDateTime as formatSharedDateTime } from '@/utils/displayDateTime';
import { Accordion, Alert, Badge, Spinner, StatusBadge } from '@/components/ui';
import type { StatusVariant } from '../../../utils/statusUtils';
import './PcrContactLogPanel.css';
import { fetchPcrContactLog, type PcrContactLogEntry } from '../../../services/pcrService';

function formatContactLogTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatSharedDateTime(d);
}

/**
 * SendGrid delivery status is an HTTP response code, not a task/PCR stage, so no vocabulary in
 * `utils/statusUtils` can key it — the colour comes from a numeric range instead. It is rendered
 * through `StatusBadge` with an explicit `variant` so it still gets the shared badge shell
 * (spacing, shrink-0, pill, icon slot) rather than a hand-rolled one.
 */
function sendGridStatusBadge(status: string | null): {
  label: string;
  variant: StatusVariant;
} {
  if (!status?.trim()) return { label: 'No status', variant: 'secondary' };
  const n = parseInt(status, 10);
  if (!Number.isNaN(n) && n >= 200 && n < 300) return { label: `Sent (${n})`, variant: 'success' };
  if (!Number.isNaN(n) && n >= 400) return { label: `Error (${n})`, variant: 'warning' };
  return { label: status, variant: 'secondary' };
}

export interface PcrContactLogPanelProps {
  submissionId: string;
  /** When false, skips fetch (lazy-load when tab inactive). */
  enabled?: boolean;
  maxHeight?: string;
  refreshToken?: number;
  className?: string;
}

const PcrContactLogPanel: React.FC<PcrContactLogPanelProps> = ({
  submissionId,
  enabled = true,
  maxHeight = 'min(38vh, 300px)',
  refreshToken = 0,
  className,
}) => {
  const [logEntries, setLogEntries] = useState<PcrContactLogEntry[]>([]);
  const [logError, setLogError] = useState<string | null>(null);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !submissionId) return;
    setLogLoading(true);
    setLogError(null);
    void fetchPcrContactLog(submissionId)
      .then((e) => setLogEntries(e))
      .catch((e: Error) => setLogError(e.message || 'Failed to load contact log'))
      .finally(() => setLogLoading(false));
  }, [submissionId, enabled, refreshToken]);

  return (
    <div className={className}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
        <h6 className="tw-text-content-muted tw-text-sm tw-mb-0">Contact log</h6>
        {!logLoading && !logError && logEntries.length > 0 && (
          <Badge bg="light" text="dark" className="tw-font-normal tw-border tw-border-line">
            {logEntries.length} {logEntries.length === 1 ? 'send' : 'sends'}
          </Badge>
        )}
      </div>
      {logLoading && (
        <div className="tw-text-content-muted tw-text-sm">
          <Spinner animation="border" size="sm" className="tw-mr-2" />
          Loading…
        </div>
      )}
      {logError && (
        <Alert variant="secondary" className="tw-text-sm tw-py-2 tw-mb-2">
          {logError}
        </Alert>
      )}
      {!logLoading && !logError && logEntries.length === 0 && (
        <p className="tw-text-sm tw-text-content-muted tw-mb-0">No outbound emails logged yet for this PCR.</p>
      )}
      {!logLoading && logEntries.length > 0 && (
        <div style={{ maxHeight, overflowY: 'auto' }}>
          <Accordion flush className="pcr-contact-log-accordion">
            {logEntries.map((e) => {
              const statusBadge = sendGridStatusBadge(e.providerStatus);
              return (
                <Accordion.Item key={e.id} eventKey={String(e.id)} className="tw-border tw-border-line tw-rounded tw-mb-1">
                  <Accordion.Header as="div" className="pcr-contact-log-header">
                    <>
                      <span
                        className="pcr-contact-log-date tw-font-semibold tw-text-black tw-text-sm"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                        title={formatContactLogTimestamp(e.createdAt)}
                      >
                        {formatContactLogTimestamp(e.createdAt)}
                      </span>
                      <StatusBadge
                        status={e.providerStatus || 'unknown'}
                        label={statusBadge.label}
                        variant={statusBadge.variant}
                        pill
                        className="pcr-contact-log-status-badge"
                      />
                    </>
                  </Accordion.Header>
                  <Accordion.Body className="tw-bg-surface-alt tw-border-t tw-border-line">
                    <dl className="tw-flex tw-flex-wrap tw-text-sm tw-mb-2 tw-text-content">
                      <dt
                        className="tw-w-3/12 sm:tw-w-2/12 tw-text-content-muted tw-mb-0"
                        style={{ fontSize: '0.65rem' }}
                      >
                        To
                      </dt>
                      <dd className="tw-w-9/12 sm:tw-w-10/12 tw-mb-0 tw-break-words">{e.recipientEmail || '–'}</dd>
                      <dt
                        className="tw-w-3/12 sm:tw-w-2/12 tw-text-content-muted tw-mb-0"
                        style={{ fontSize: '0.65rem' }}
                      >
                        Subject
                      </dt>
                      <dd className="tw-w-9/12 sm:tw-w-10/12 tw-mb-0 tw-break-words">{e.subject || '–'}</dd>
                      <dt
                        className="tw-w-3/12 sm:tw-w-2/12 tw-text-content-muted tw-mb-0"
                        style={{ fontSize: '0.65rem' }}
                      >
                        From
                      </dt>
                      <dd className="tw-w-9/12 sm:tw-w-10/12 tw-mb-0 tw-break-words">
                        {e.actorName}
                        {e.templateName ? ` · ${e.templateName}` : ''}
                      </dd>
                    </dl>
                    {e.providerMessageId ? (
                      <div className="tw-mb-2">
                        <div className="tw-text-sm tw-text-content-muted tw-mb-0">Provider message ID</div>
                        <code className="tw-text-sm tw-block tw-break-words tw-bg-surface tw-border tw-border-line tw-rounded tw-px-2 tw-py-1">
                          {e.providerMessageId}
                        </code>
                      </div>
                    ) : null}
                    <div className="tw-text-sm tw-text-content-muted tw-mb-1">Message preview</div>
                    {e.bodySnippet ? (
                      <pre
                        className="tw-mb-0 tw-rounded tw-border tw-border-line tw-bg-surface tw-px-2 tw-py-2 tw-text-content tw-font-mono tw-text-sm"
                        style={{
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {e.bodySnippet}
                      </pre>
                    ) : (
                      <p className="tw-text-content-muted tw-italic tw-mb-0 tw-text-sm">
                        No message preview is stored for this send.
                      </p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default PcrContactLogPanel;
